import AppCommandBar from 'app/AppCommandBar'
import { AutoStack, Text2 } from 'components'
import { RichTextViewer } from 'components/RichTextViewer/RichTextViewer'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { NotificationDialog } from 'dialogs/NotificationDialog'
import { YesNoDialog } from 'dialogs/YesNoDialog'
import { FormikActions } from 'formik'
import {
  ProvingDetailsExternalVehicleForm,
  ProvingDetailsExternalVehicleFormSchema,
  ProvingDetailsInternalVehicleForm,
  ProvingDetailsInternalVehicleFormSchema,
  ProvingDetailsOrderIdForm,
  ProvingDetailsTimespanForm,
  ProvingDetailsTimespanFormSchema,
  ProvingDetailsTransportForm,
  ProvingDetailsUnknownVehicleForm,
  ProvingDetailsUnknownVehicleFormSchema,
} from 'forms/ProvingDetailsForm'
import { ProvingDetailsAnnotationForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAnnotationForm'
import { ProvingDetailsGroupForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsGroupForm'
import {
  ProvingTransportDetailsForm,
  ProvingTransportDetailsFormSchema,
  TransportDetailsFormValues,
} from 'forms/ProvingDetailsForm/shared/ProvingTransportDetailsForm'
import { ProvingDetailsExistingVehiclesForm } from 'forms/ProvingDetailsForm/vehicles/ProvingDetailsExistingVehiclesForm'
import {
  ProvingVehicleDetailsFormSchema,
  VehicleDetailsForm,
} from 'forms/ProvingDetailsForm/vehicles/VehicleDetailsForm'
import {
  canUpdateProving,
  isInProvingRole,
  omitDeep,
  removeNullProperties,
} from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import {
  useCommand,
  useDeselectCommand,
  useGroupCommand,
} from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import { pick } from 'lodash'
import {
  BookingStatus,
  ExternalVehicle,
  InternalVehicle,
  LicensePlateType,
  ProvingUserRole,
  TransportType,
  UserRole,
  Vehicle,
  VehicleBatchInput,
  VehicleInsertInput,
  VehicleType,
  VehicleUpdateInput,
} from 'model'
import { IconButton, PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Page, Section } from './Layout'
import {
  useAddVehicleMutation,
  useAddVehiclesMutation,
  useDeleteVehiclesMutation,
  useExportToSapMutation,
  useUpdateProvingMutation,
  useUpdateVehiclesMutation,
} from './hooks/index'
import { VehicleList } from './lists/index'

enum FormType {
  addVehicle,
  editInternalVehicle,
  editExternalVehicle,
  timespan,
  orderId,
  transport,
  transportStatus,
  annotation,
}

export const PageVehicles: React.FC = () => {
  const { t } = useTranslation()
  const [proving, refetch] = useCurrentProving()
  const vehicles = proving.vehicles || []
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isProvingAdmin = isInProvingRole(proving, ProvingUserRole.Administrator)
  const isProvingEditor = isInProvingRole(proving, ProvingUserRole.Editor)
  const isProvingResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  let canUpdateCurrentProving = canUpdateProving(proving)
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )

  const [sapExportLoading, setSapExportLoading] = React.useState(false)

  const canUseCommand = canUpdateCurrentProving && !isMaintenanceHead
  const canUpdateStatus = () => {
    if (
      isProvingAdmin ||
      isAdmin ||
      isProvingResourceManager ||
      isProvingEditor ||
      isServiceTeam
    ) {
      return true
    } else {
      return false
    }
  }

  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()
  const [
    transportPanelState,
    // showTransportPanel,
    hideTransportPanel,
  ] = useFormikPanelState()
  const [
    internalVehiclePanelState,
    showInternalVehiclePanel,
    hideInternalVehiclePanel,
  ] = useFormikPanelState()
  const [
    externalVehiclePanelState,
    showExternalVehiclePanel,
    hideExternalVehiclePanel,
  ] = useFormikPanelState()
  const [
    unknownVehiclesPanelState,
    showUnknownVehiclesPanel,
    hideUnknownVehiclesPanel,
  ] = useFormikPanelState()
  const [updatePanelState, showUpdatePanel, hideUpdatePanel] =
    useFormikPanelState()
  const [editPanelState, showEditPanel, hideEditPanel] = useFormikPanelState()
  const [editDetailsPanelState, showEditDetailsPanel, hideEditDetailsPanel] =
    useFormikPanelState()
  const [
    existingVehiclePanelState,
    showExistingVehiclePanel,
    hideExistingVehiclePanel,
  ] = useFormikPanelState()
  const [addGroupPanelState, showAddGroupPanel, hideAddGroupPanel] =
    useFormikPanelState()
  const [annotationPanelState, showAnnotationPanel, hideAnnotationPanel] =
    useFormikPanelState()
  const [
    transportDataPanelState,
    showTransportDataPanel,
    hideTransportDataPanel,
  ] = useFormikPanelState()
  const [deleteDialogState, showDeleteDialog, hideDeleteDialog] =
    useDialogState()
  const [confirmTpoDialogState, showConfirmTpoDialog, hideConfirmTpoDialog] =
    useDialogState()
  const [tpoSuccessDialogState, showTpoSuccessDialog, hideTpoSuccessDialog] =
    useDialogState()

  const [addVehicle] = useAddVehicleMutation()
  const [addVehicles] = useAddVehiclesMutation()
  const [deleteVehicles] = useDeleteVehiclesMutation()
  const [updateVehicles] = useUpdateVehiclesMutation()
  const [exportToSAP] = useExportToSapMutation()
  const [, setSelectedItemId] = React.useState()
  const [updateProving] = useUpdateProvingMutation()
  const [selection, selectedItems] = useSelection<Vehicle>({
    getKey: (item) => item._id,
  })

  const canCreateTPO = () => {
    return (
      isAdmin || isProvingResourceManager || isProvingEditor || isServiceTeam
    )
  }

  const canExportToSAP = React.useCallback(() => {
    if (
      isAdmin ||
      isProvingResourceManager ||
      isProvingEditor ||
      isServiceTeam
    ) {
      if (selectedItems.length > 0) {
        return selectedItems.every(
          (vehicle) =>
            !!(
              !vehicle.exportedToSAP &&
              !vehicle.unknown &&
              vehicle.transport &&
              ((vehicle.transport.outboundTransportSender &&
                vehicle.transport.outboundTransportRecipient) ||
                (vehicle.transport.returnTransportSender &&
                  vehicle.transport.returnTransportRecipient))
            )
        )
      }
    }
    return false
  }, [
    isAdmin,
    isProvingResourceManager,
    isProvingEditor,
    isServiceTeam,
    selectedItems,
  ])

  const [formType, setFormType] = React.useState()
  const [editedVehicleId, setEditedVehicleId] = React.useState()

  const handleVehicleSubmit = React.useCallback(
    async (values: VehicleInsertInput, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const updatedValues = removeNullProperties({
        ...values,
      })
      const inputs = {
        transport: {
          bookingStatus: BookingStatus.NotRequired,
          transportType: TransportType.NoTransport,
        },
        ...updatedValues,
      }

      await addVehicle({
        variables: {
          provingId: proving.id,
          input: inputs,
        },
      })
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideInternalVehiclePanel()
      hideExternalVehiclePanel()
    },
    [
      hideInternalVehiclePanel,
      hideExternalVehiclePanel,
      proving,
      addVehicle,
      refetch,
      selection,
    ]
  )
  const handleExistingVehiclesSubmit = React.useCallback(
    async (
      values: { vehicles: VehicleInsertInput[] },
      actions: FormikActions<any>
    ) => {
      actions.setSubmitting(true)

      await addVehicles({
        variables: {
          provingId: proving.id,
          inputs: values.vehicles.map((v) => ({
            ...pick(v, [
              'type',
              'unknown',
              'model',
              'licensePlate',
              'licensePlateType',
              'vin',
              'internalNumberUnknown',
              'eqVehicle',
              'label',
              'labelExternal',
            ]),
            transport: {
              bookingStatus: BookingStatus.NotRequired,
              transportType: TransportType.NoTransport,
            },
            dateFrom: proving.dateFrom,
            dateTo: proving.dateTo,
          })),
        },
      })

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideExistingVehiclePanel()
    },
    [hideExistingVehiclePanel, refetch, selection, addVehicles, proving]
  )
  const handleSubmitUnknowns = React.useCallback(
    async (
      values: { amount: number; model: string },
      actions: FormikActions<any>
    ) => {
      actions.setSubmitting(true)

      const { amount, model } = values
      const inputs: VehicleInsertInput[] = []

      for (let i = 0; i < amount; i++) {
        const vehicle: Vehicle = {
          unknown: true,
          model,
          type: VehicleType.Internal,
          internalNumberUnknown: true,
          transport: {
            bookingStatus: BookingStatus.NotRequired,
            transportType: TransportType.NoTransport,
          },
          licensePlateType: LicensePlateType.Unknown,
          dateTo: proving.dateTo,
          dateFrom: proving.dateFrom,
        } as any
        inputs.push(vehicle)
      }

      await addVehicles({
        variables: {
          provingId: proving.id,
          inputs,
        },
      })

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideUnknownVehiclesPanel()
    },
    [hideUnknownVehiclesPanel, addVehicles, proving, selection, refetch]
  )
  const handleSubmitEdit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)

      if (values.licensePlateType !== 'known') {
        values.licensePlate = ''
      }
      if (values.eqVehicle === '') {
        values.eqVehicle = null
      }
      const batch: VehicleBatchInput[] = [
        {
          _id: editedVehicleId || values._id,
          update: removeNullProperties(
            omitDeep(values, ['_id', '_changes', 'exportedToSAP', 'transport'])
          ),
        },
      ]

      await updateVehicles({ variables: { batch, provingId: proving.id } })

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideEditPanel()
      hideTimespanPanel()
      hideEditDetailsPanel()
    },
    [
      hideEditPanel,
      hideTimespanPanel,
      hideEditDetailsPanel,
      refetch,
      proving,
      updateVehicles,
      editedVehicleId,
      selection,
    ]
  )
  const handleSubmitUpdate = React.useCallback(
    async (values: VehicleUpdateInput, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const batch: VehicleBatchInput[] = selectedItems.map((sel) => {
        const updateItem = {
          _id: sel._id,
          update: values,
        }
        // Replace transport delivery (outboundTransportRecipientDate)
        // and pickup (returnTransportSenderDate) date with vehicle dates if set,
        // so that they are always the same
        if (values.dateFrom && values.dateTo) {
          if (sel.transport) {
            if (!updateItem.update.transport) {
              updateItem.update.transport = {}
            }
            if (sel.transport.outboundTransportRecipientDate) {
              updateItem.update.transport.outboundTransportRecipientDate =
                values.dateFrom
            }
            if (sel.transport.returnTransportSenderDate) {
              updateItem.update.transport.returnTransportSenderDate =
                values.dateTo
            }
          }
        }

        return updateItem
      })
      await updateVehicles({ variables: { batch, provingId: proving.id } })

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideUpdatePanel()
      hideTimespanPanel()
    },
    [
      hideUpdatePanel,
      selection,
      selectedItems,
      refetch,
      proving,
      updateVehicles,
      hideTimespanPanel,
    ]
  )

  const handleTransportSubmit = React.useCallback(
    async (values: VehicleUpdateInput, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      let newValues: VehicleUpdateInput = values

      if (
        (newValues.transport &&
          newValues.transport.bookingStatus === BookingStatus.NotRequired &&
          newValues.transport &&
          newValues.transport.transportType !== TransportType.NoTransport) ||
        !(newValues.transport && newValues.transport.bookingStatus)
      ) {
        newValues.transport
          ? (newValues.transport.bookingStatus = BookingStatus.Open)
          : (newValues = values)
      } else {
        newValues = values
      }

      const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: newValues,
      }))

      await updateVehicles({ variables: { batch, provingId: proving.id } })

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideTransportPanel()
    },
    [
      selection,
      selectedItems,
      refetch,
      proving,
      updateVehicles,
      hideTransportPanel,
    ]
  )

  const handleConfirmDelete = React.useCallback(async () => {
    await deleteVehicles({
      variables: {
        provingId: proving.id,
        idsToDelete: selectedItems.map((sel) => sel._id),
      },
    })
    selection.setAllSelected(false)
    refetch()
    hideDeleteDialog()
  }, [
    hideDeleteDialog,
    selection,
    selectedItems,
    proving,
    deleteVehicles,
    refetch,
  ])

  const handleGroupAssign = React.useCallback(
    async (
      values: { group: string },
      actions: FormikActions<{ group: string }>
    ) => {
      actions.setSubmitting(true)

      const batch: VehicleBatchInput[] = selectedItems.map((v) => ({
        _id: v._id,
        update: {
          group: values.group,
        },
      }))

      await updateVehicles({
        variables: {
          provingId: proving.id,
          batch,
        },
      })

      selection.setAllSelected(false)
      refetch()
      actions.setSubmitting(false)
      hideAddGroupPanel()
    },
    [
      selection,
      selectedItems,
      refetch,
      hideAddGroupPanel,
      proving,
      updateVehicles,
    ]
  )
  const handleRemoveGroup = React.useCallback(async () => {
    const batch: VehicleBatchInput[] = selectedItems.map((v) => ({
      _id: v._id,
      update: {
        group: '',
      },
    }))

    await updateVehicles({
      variables: {
        provingId: proving.id,
        batch,
      },
    })
    selection.setAllSelected(false)
    refetch()
  }, [proving, selectedItems, updateVehicles, refetch, selection])

  useCommand(
    {
      key: 'addVehicle',
      text: t('model.proving.vehicle.addVehicle'),
      iconProps: { iconName: 'Car' },
      headerText: t('model.proving.vehicle.addVehicle'),
      disabled: !canUseCommand,
      subMenuProps: {
        items: [
          {
            key: 'addInternalVehicle',
            text: t('model.proving.vehicle.addInternalVehicle'),
            onClick: () => {
              setFormType(FormType.addVehicle)
              showInternalVehiclePanel({
                dateTo: proving.dateTo,
                dateFrom: proving.dateFrom,
                type: VehicleType.Internal,
                unknown: false,
                licensePlateType: LicensePlateType.Known,
                internalNumberUnknown: false,
              })
            },
          },
          {
            key: 'addExternalVehicle',
            text: t('model.proving.vehicle.addExternalVehicle'),
            onClick: () => {
              setFormType(FormType.addVehicle)
              showExternalVehiclePanel({
                dateTo: proving.dateTo,
                dateFrom: proving.dateFrom,
                type: VehicleType.External,
                unknown: false,
                licensePlateType: LicensePlateType.Known,
              })
            },
          },
          {
            key: 'addUnknownVehicle',
            text: t('model.proving.vehicle.addUnknownVehicle'),
            onClick: () => {
              setFormType(FormType.addVehicle)
              showUnknownVehiclesPanel({ amount: 1 })
            },
          },
          {
            key: 'addExistingVehicle',
            text: t('model.proving.vehicle.addExistingVehicle'),
            subMenuProps: {
              items: [
                {
                  key: 'addVehicleFromProving',
                  text: t('model.proving.vehicle.addVehicleFromProving'),
                  onClick: () => {
                    showExistingVehiclePanel({ vehicles: [] })
                  },
                },
              ],
            },
          },
        ],
      },
    },
    [selectedItems]
  )
  useCommand(
    {
      key: 'editVehicle',
      disabled: selectedItems.length !== 1 || !canUseCommand,
      text: t('model.proving.vehicle.editVehicle'),
      iconProps: { iconName: 'Edit' },
      headerText: t('model.proving.vehicle.editVehicle'),
      onClick: () => {
        const { _id, ...vehicle } = selectedItems[0] as InternalVehicle &
          ExternalVehicle
        const eqVehicleId = vehicle.eqVehicle ? vehicle.eqVehicle._id : null
        setEditedVehicleId(_id)
        if (vehicle.type === VehicleType.Internal) {
          setFormType(FormType.editInternalVehicle)
          showEditPanel(
            removeNullProperties({
              ...omitDeep(vehicle, ['_changes', '__typename', 'transport']),
              eqVehicle: eqVehicleId,
            })
          )
        } else {
          setFormType(FormType.editExternalVehicle)
          showEditPanel(
            omitDeep(vehicle, ['_changes', '__typename', 'transport'])
          )
        }
      },
    },
    [selectedItems, setEditedVehicleId]
  )

  //#region TIMESPAN
  const setTimespanDateToProvingDate = React.useCallback(async () => {
    const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: proving.dateFrom,
        dateTo: proving.dateTo,
      },
    }))

    await updateVehicles({
      variables: {
        provingId: proving.id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateVehicles, refetch])

  useCommand(
    {
      key: 'timespan',
      disabled: selectedItems.length === 0 || !canUseCommand,
      text: t('model.proving.vehicle.timespan'),
      iconProps: { iconName: 'CalendarWeek' },
      subMenuProps: {
        items: [
          {
            key: 'setTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              selectedItems.length > 1
                ? showTimespanPanel({})
                : showTimespanPanel({
                    dateTo: selectedItems[0].dateTo,
                    dateFrom: selectedItems[0].dateFrom,
                  })
              setFormType({ FormType: FormType.timespan })
            },
          },
          {
            key: 'setDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setTimespanDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems]
  )

  //#region ORDERID
  const setNoOrderId = React.useCallback(async () => {
    const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        orderId: '',
      },
    }))

    await updateVehicles({
      variables: {
        provingId: proving.id,
        batch,
      },
    })

    refetch()
  }, [proving, selectedItems, updateVehicles, refetch])
  useCommand(
    {
      key: 'orderId',
      disabled: selectedItems.length === 0 || !canUseCommand,
      text: t('model.proving.vehicle.orderId'),
      iconProps: { iconName: 'DonutChart' },
      headerText: t('model.proving.vehicle.orderId'),
      subMenuProps: {
        items: [
          {
            key: 'assignOrderId',
            text: t('field.orderId.assignOrderId'),
            onClick: () => {
              selectedItems.length > 1
                ? showUpdatePanel({})
                : showUpdatePanel({
                    orderId: selectedItems[0].orderId,
                  })
              setFormType({ FormType: FormType.orderId })
            },
          },
          {
            key: 'noOrderId',
            text: t('field.orderId.noOrderId'),
            onClick: () => {
              setNoOrderId()
            },
          },
        ],
      },
    },
    [selectedItems]
  )
  //#endregion ORDERID
  //#region TRANSPORT
  const handleTransportBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          transport: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateVehicles({
        variables: {
          batch,
          provingId: proving.id,
        },
      })

      refetch()
    }, [proving, selectedItems, updateVehicles, refetch])

  const handleTransportBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        transport: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateVehicles({
      variables: {
        batch,
        provingId: proving.id,
      },
    })

    refetch()
  }, [proving, selectedItems, updateVehicles, refetch])

  const handleTransportBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          transport: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateVehicles({
        variables: {
          batch,
          provingId: proving.id,
        },
      })

      refetch()
    }, [proving, selectedItems, updateVehicles, refetch])

  const handleTransportBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          transport: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))

      await updateVehicles({
        variables: {
          batch,
          provingId: proving.id,
        },
      })

      refetch()
    }, [proving, selectedItems, updateVehicles, refetch])

  const handleNoTransport = React.useCallback(async () => {
    const batch: VehicleBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        transport: {
          bookingStatus: BookingStatus.NotRequired,
          transportType: TransportType.NoTransport,
        },
      },
    }))

    await updateVehicles({
      variables: {
        batch,
        provingId: proving.id,
      },
    })

    refetch()
  }, [proving, selectedItems, updateVehicles, refetch])

  const getDefaultTransportData = (
    transportType: TransportType,
    selectedItem: Vehicle
  ) => {
    const provingGround = proving.provingGround
    if (!provingGround) return undefined

    const transport = selectedItem.transport

    const getTransportData = (type: string) => ({
      [`${type}TransportSender`]:
        transport?.[`${type}TransportSender`] ??
        provingGround[`${type}TransportSender`],
      [`${type}TransportSenderTime`]: transport?.[`${type}TransportSenderTime`],
      [`${type}TransportSenderContact`]:
        transport?.[`${type}TransportSenderContact`] ?? '',
      [`${type}TransportSenderDate`]:
        type === 'return'
          ? selectedItem.dateTo
          : transport?.[`${type}TransportSenderDate`],
      [`${type}TransportRecipient`]:
        transport?.[`${type}TransportRecipient`] ??
        provingGround[`${type}TransportRecipient`],
      [`${type}TransportRecipientTime`]:
        transport?.[`${type}TransportRecipientTime`],
      [`${type}TransportRecipientContact`]:
        transport?.[`${type}TransportRecipientContact`] ?? '',
      [`${type}TransportRecipientDate`]:
        type === 'return'
          ? transport?.returnTransportRecipientDate
          : selectedItem.dateFrom,
    })

    let res = {}

    const getEmptyTransportData = (type: string) => ({
      [`${type}TransportSender`]: '',
      [`${type}TransportSenderTime`]: '',
      [`${type}TransportSenderDate`]: null,
      [`${type}TransportRecipient`]: '',
      [`${type}TransportRecipientTime`]: '',
      [`${type}TransportRecipientDate`]: null,
      [`${type}TransportRecipientContact`]: '',
    })

    switch (transportType) {
      case TransportType.OutboundAndReturnTransport:
        res = {
          ...getTransportData('outbound'),
          ...getTransportData('return'),
        }
        break
      case TransportType.OutboundTransport:
        res = {
          ...getTransportData('outbound'),
          ...getEmptyTransportData('return'),
        }
        break
      case TransportType.ReturnTransport:
        res = {
          ...getEmptyTransportData('outbound'),
          ...getTransportData('return'),
        }
        break
      default:
        break
    }

    return res
  }

  useCommand(
    {
      key: 'transport',
      disabled: selectedItems.length === 0 || !canUseCommand,
      text: t('model.proving.vehicle.transport'),
      iconProps: { iconName: 'Train' },
      headerText: t('model.proving.vehicle.transport'),
      subMenuProps: {
        items: [
          // {
          //   key: 'setTransport',
          //   text: t('field.vehicle.setTransport.label'),
          //   onClick: () => {
          //     selectedItems.length > 1
          //       ? showTransportPanel({})
          //       : showTransportPanel({
          //           transport: omitDeep(
          //             selectedItems[0].transport,
          //             '__typename'
          //           ),
          //         })
          //     setFormType({ FormType: FormType.transport })
          //   },
          // },
          {
            key: 'outboundAndReturnTransport',
            text: t('enum.transport.outboundAndReturnTransport'),
            onClick: () => {
              showTransportDataPanel({
                transportType: TransportType.OutboundAndReturnTransport,
                ...getDefaultTransportData(
                  TransportType.OutboundAndReturnTransport,
                  selectedItems[0]
                ),
              })
            },
          },
          {
            key: 'outboundTransport',
            text: t('enum.transport.outboundTransport'),
            onClick: () => {
              showTransportDataPanel({
                transportType: TransportType.OutboundTransport,
                ...getDefaultTransportData(
                  TransportType.OutboundTransport,
                  selectedItems[0]
                ),
              })
            },
          },
          {
            key: 'returnTransport',
            text: t('enum.transport.returnTransport'),
            onClick: () => {
              showTransportDataPanel({
                transportType: TransportType.ReturnTransport,
                ...getDefaultTransportData(
                  TransportType.ReturnTransport,
                  selectedItems[0]
                ),
              })
            },
          },
          {
            key: 'noTransport',
            text: t('enum.transport.noTransport'),
            onClick: () => {
              handleNoTransport()
            },
          },
          {
            key: 'changeStatus',
            text: t('field.status.changeStatus.label'),
            disabled: !canUpdateStatus(),
            subMenuProps: {
              items: [
                {
                  key: 'vehicleTransportBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleTransportBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'vehicleTransportBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleTransportBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'vehicleTransportBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleTransportBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'vehicleTransportBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleTransportBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
            // onClick: () => {
            //   selectedItems.length > 1
            //     ? showTransportStatusPanel({})
            //     : showTransportStatusPanel({
            //         transport: {
            //           bookingStatus: selectedItems[0].transport!.bookingStatus,
            //         },
            //       })
            //   setFormType({ FormType: FormType.transportStatus })
            // },
          },
        ],
      },
    },
    [selectedItems]
  )
  //#endregion TRANSPORT

  useGroupCommand(
    t,
    proving,
    selectedItems,
    () => {
      showAddGroupPanel({ group: selectedItems[0].group })
    },
    handleRemoveGroup,
    selectedItems.length === 0 || !canUpdateCurrentProving
  )
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 0,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  useCommand(
    {
      key: 'delete',
      priority: 10,
      farCommand: true,
      text: t('model.proving.vehicle.deleteVehicle'),
      hidden: selectedItems.length === 0,
      disabled: !canUseCommand,
      iconProps: { iconName: 'Delete' },
      onClick: () => showDeleteDialog(),
    },
    [selectedItems]
  )

  const handleTransportDataSubmit = React.useCallback(
    async (values: TransportDetailsFormValues, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((vehicle) => vehicle._id)
      const batch: VehicleBatchInput[] = selectedItems.map((sel) =>
        sel.transport &&
        sel.transport.bookingStatus !== BookingStatus.NotRequired
          ? {
              _id: sel._id,
              update: {
                transport: {
                  ...values,
                },
              },
            }
          : {
              _id: sel._id,
              update: {
                transport: {
                  bookingStatus: BookingStatus.Open,
                  ...values,
                },
              },
            }
      )

      await updateVehicles({
        variables: {
          batch,
          provingId: proving.id,
        },
      }).then((_) => refetch())

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      hideTransportDataPanel()
    },
    [proving, selectedItems, refetch, updateVehicles, hideTransportDataPanel]
  )

  const handleSapExport = React.useCallback(async () => {
    setSapExportLoading(true)
    for (const vehicle of selectedItems) {
      const vehicleId = vehicle._id
      if (vehicle && vehicleId && vehicle.transport) {
        await exportToSAP({
          variables: {
            provingId: proving._id,
            vehicleId,
            outboundTransportSender: vehicle.transport.outboundTransportSender
              ? vehicle.transport.outboundTransportSender.supplierNumber
              : undefined,
            outboundTransportRecipient: vehicle.transport
              .outboundTransportRecipient
              ? vehicle.transport.outboundTransportRecipient.supplierNumber
              : undefined,
            returnTransportSender: vehicle.transport.returnTransportSender
              ? vehicle.transport.returnTransportSender.supplierNumber
              : undefined,
            returnTransportRecipient: vehicle.transport.returnTransportRecipient
              ? vehicle.transport.returnTransportRecipient.supplierNumber
              : undefined,
          },
        })
      }
      setSapExportLoading(false)
      hideConfirmTpoDialog()
      showTpoSuccessDialog()
    }
    selection.setAllSelected(false)
    refetch()
    setSapExportLoading(false)
    hideConfirmTpoDialog()
  }, [
    selectedItems,
    exportToSAP,
    refetch,
    showTpoSuccessDialog,
    hideConfirmTpoDialog,
    proving._id,
    selection,
  ])

  const exportToSAPLabel = () => {
    if (selectedItems.length === 1 && !selectedItems[0].unknown) {
      if (
        selectedItems[0] &&
        (selectedItems[0] as any).exportedToSAP === true
      ) {
        return t('model.proving.vehicle.alreadyExportedToSAP')
      } else {
        return t('model.proving.vehicle.exportToSAP')
      }
    } else {
      return t('model.proving.vehicle.exportToSAP')
    }
  }

  const VehicleConfirmationText = (vehicle: Vehicle) => {
    if (
      vehicle &&
      !vehicle.unknown &&
      vehicle.transport &&
      vehicle.transport.transportType
    ) {
      const label =
        (vehicle as InternalVehicle).label ||
        (vehicle as ExternalVehicle).labelExternal ||
        ''
      const outboundSender = vehicle.transport.outboundTransportSender
        ? vehicle.transport.outboundTransportSender.place
        : ''
      const outboundRecipient = vehicle.transport.outboundTransportRecipient
        ? vehicle.transport.outboundTransportRecipient.place
        : ''
      const returnSender = vehicle.transport.returnTransportSender
        ? vehicle.transport.returnTransportSender.place
        : ''
      const returnRecipient = vehicle.transport.returnTransportRecipient
        ? vehicle.transport.returnTransportRecipient.place
        : ''
      switch (vehicle.transport.transportType) {
        case TransportType.OutboundTransport:
          return (
            <div>
              {label}
              {': '}
              {t(`enum.transportType.${vehicle.transport.transportType}`)}
              <br />
              {outboundSender}
              {' - '}
              {outboundRecipient}
            </div>
          )
        case TransportType.ReturnTransport:
          return (
            <div>
              {label}
              {': '}
              {t(`enum.transportType.${vehicle.transport.transportType}`)}
              <br />
              {returnSender}
              {' - '}
              {returnRecipient}
            </div>
          )
        case TransportType.OutboundAndReturnTransport:
          return (
            <div>
              {label}
              {': '}
              {t(`enum.transportType.${vehicle.transport.transportType}`)}
              <br />
              {outboundSender}
              {' - '}
              {outboundRecipient}
              <br />
              {returnSender}
              {' - '}
              {returnRecipient}
            </div>
          )
        default:
          break
      }
    }
    return <></>
  }

  const SapConfirmationText = (() => (
    <div>
      {selectedItems.map((sel, i) => (
        <>
          {VehicleConfirmationText(sel)}
          {i === selectedItems.length - 1 ? <></> : <hr />}
        </>
      ))}
    </div>
  ))()

  useCommand(
    {
      key: 'exportToSap',
      priority: 20,
      farCommand: true,
      text: exportToSAPLabel(),
      iconProps: { iconName: 'Export' },
      hidden: !canCreateTPO(),
      disabled: !canExportToSAP(),
      onClick: () => {
        showConfirmTpoDialog()
      },
    },
    [selectedItems]
  )

  // const handleDetailsViewSubmit = React.useCallback(
  //   async (values: any, actions: FormikActions<any>) => {
  //     actions.setSubmitting(true)
  //     // const { errors } = await updateProvingMaterials({
  //     //   variables: {
  //     //     provingId: proving.id,
  //     //     batch: [
  //     //       {
  //     //         _id: selectedItemId,
  //     //         update: omitDeep(values, ['__typename']),
  //     //       },
  //     //     ],
  //     //   },
  //     // })
  //     // if (errors) {
  //     //   throw errors
  //     // }
  //     dbg(selectedItemId)
  //     actions.setSubmitting(false)
  //     selection.setAllSelected(false)
  //     refetch()
  //     hideEditDetailsPanel()
  //   },
  //   [hideEditDetailsPanel, proving, refetch, selection]
  // )

  const handleAnnotationSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const { errors } = await updateProving({
        variables: {
          id: proving.id,
          update: {
            annotationVehicles: values.annotation,
          },
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      refetch()
      hideAnnotationPanel()
    },
    [proving, updateProving, hideAnnotationPanel, refetch]
  )

  const onItemInvoked = (item: any): void => {
    setSelectedItemId(item._id)
    showEditDetailsPanel({
      ...omitDeep(item, ['__typename']),
      eqVehicle: item.eqVehicle ? item.eqVehicle._id : '',
    })
  }

  return (
    <Page>
      <Section label={t('section.vehicles.annotation')}>
        <AutoStack horizontal={true}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            disabled={!canUseCommand}
            onClick={() => {
              setFormType({ formType: FormType.annotation })
              showAnnotationPanel({
                annotation: proving.annotationVehicles || '',
              })
            }}
          />

          <RichTextViewer
            richText={
              proving.annotationVehicles ? proving.annotationVehicles : ''
            }
          />
        </AutoStack>
      </Section>
      {proving.provingGround!.noteHeaderVehicles && (
        <Section label={proving.provingGround!.noteHeaderVehicles}>
          <Text2>{proving.provingGround!.noteVehicles}</Text2>
        </Section>
      )}
      <Section label={t('section.vehicles.header')}>
        <AppCommandBar />
        <VehicleList
          items={vehicles}
          selection={selection}
          onItemInvoked={onItemInvoked}
        />
      </Section>
      <FormikPanel
        {...internalVehiclePanelState}
        onSubmit={handleVehicleSubmit}
        validationSchema={new ProvingDetailsInternalVehicleFormSchema()}
      >
        <ProvingDetailsInternalVehicleForm />
      </FormikPanel>
      <FormikPanel {...annotationPanelState} onSubmit={handleAnnotationSubmit}>
        {formType !== undefined &&
          formType.formType === FormType.annotation && (
            <ProvingDetailsAnnotationForm />
          )}
      </FormikPanel>
      <FormikPanel
        {...externalVehiclePanelState}
        onSubmit={handleVehicleSubmit}
        validationSchema={new ProvingDetailsExternalVehicleFormSchema()}
      >
        <ProvingDetailsExternalVehicleForm />
      </FormikPanel>
      <FormikPanel
        {...unknownVehiclesPanelState}
        onSubmit={handleSubmitUnknowns}
        validationSchema={new ProvingDetailsUnknownVehicleFormSchema()}
      >
        <ProvingDetailsUnknownVehicleForm />
      </FormikPanel>
      <FormikPanel
        {...editPanelState}
        onSubmit={handleSubmitEdit}
        validationSchema={
          formType === FormType.editInternalVehicle
            ? new ProvingDetailsInternalVehicleFormSchema()
            : new ProvingDetailsExternalVehicleFormSchema()
        }
      >
        {formType === FormType.editInternalVehicle && (
          <ProvingDetailsInternalVehicleForm
            editVehicle={selectedItems[0] as InternalVehicle}
          />
        )}
        {formType === FormType.editExternalVehicle && (
          <ProvingDetailsExternalVehicleForm />
        )}
      </FormikPanel>
      <FormikPanel
        {...timespanPanelState}
        onSubmit={handleSubmitUpdate}
        validationSchema={
          new ProvingDetailsTimespanFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formType !== undefined && formType.FormType === FormType.timespan && (
          <ProvingDetailsTimespanForm proving={proving} />
        )}
      </FormikPanel>
      <FormikPanel {...updatePanelState} onSubmit={handleSubmitUpdate}>
        {formType !== undefined && formType.FormType === FormType.orderId && (
          <ProvingDetailsOrderIdForm
            orderIds={proving.orderIdDistribution!.map((x: any) => x)}
          />
        )}
      </FormikPanel>
      <FormikPanel {...transportPanelState} onSubmit={handleTransportSubmit}>
        {formType !== undefined && formType.FormType === FormType.transport && (
          <ProvingDetailsTransportForm />
        )}
      </FormikPanel>
      <FormikPanel
        {...existingVehiclePanelState}
        type={PanelType.large}
        onSubmit={handleExistingVehiclesSubmit}
      >
        <ProvingDetailsExistingVehiclesForm />
      </FormikPanel>
      <FormikPanel
        {...addGroupPanelState}
        onSubmit={handleGroupAssign}
        headerText={'Gruppe zuordnen'}
      >
        <ProvingDetailsGroupForm />
      </FormikPanel>
      <FormikPanel
        {...editDetailsPanelState}
        onSubmit={handleSubmitEdit}
        type={PanelType.large}
        validationSchema={new ProvingVehicleDetailsFormSchema()}
      >
        <VehicleDetailsForm editVehicle={selectedItems[0] as InternalVehicle} />
      </FormikPanel>
      <FormikPanel
        {...transportDataPanelState}
        type={PanelType.large}
        validationSchema={ProvingTransportDetailsFormSchema}
        headerText={'Transportinformationen'}
        onSubmit={handleTransportDataSubmit}
      >
        <ProvingTransportDetailsForm
          annotation={
            'Fahrzeuge dürfen erst zugestellt werden, wenn der Erprobungsleiter vor Ort ist.'
          }
          disableDeliveryAndPickupDate={true}
          dateFrom={selectedItems[0] && selectedItems[0].dateFrom}
          dateTo={selectedItems[0] && selectedItems[0].dateTo}
        />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...deleteDialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
      <YesNoDialog
        {...confirmTpoDialogState}
        onConfirm={handleSapExport}
        onDismiss={hideConfirmTpoDialog}
        dialogContentProps={{
          title: 'Möchten Sie folgende Transportaufträge erstellen?',
        }}
        yesButtonText={t('action.createTpo')}
        noButtonText={t('action.cancel')}
        isLoading={sapExportLoading}
      >
        {SapConfirmationText}
      </YesNoDialog>
      <NotificationDialog
        {...tpoSuccessDialogState}
        onDismiss={hideTpoSuccessDialog}
        titleText={'Transportinformationen wurden versendet'}
      />
      {/* <MaterialList items={[]} /> */}
      {/* <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}
    </Page>
  )
}
